import { useEffect, useState, useCallback } from "react";
import { Bell, Calendar, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch (err) {
      console.error("notifications load failed", err);
    }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async () => {
    try {
      await api.post("/notifications/read");
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error("mark read failed", err);
    }
  };

  const openItem = (n) => {
    if (n.link) navigate(n.link);
  };

  if (!user) return null;

  return (
    <DropdownMenu onOpenChange={(o) => o && unread > 0 && markRead()}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notifications-bell"
          aria-label="Notifications"
          className="relative w-10 h-10 rounded-full glass flex items-center justify-center hover:scale-110 transition-transform"
        >
          <Bell className="w-4 h-4" strokeWidth={1.8} />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 p-0 rounded-2xl border-border overflow-hidden"
        data-testid="notifications-panel"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="font-display font-semibold">Notifications</div>
          {items.length > 0 && (
            <button onClick={markRead} className="text-xs text-primary hover:underline flex items-center gap-1">
              <Check className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
              You're all caught up.
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.notification_id}
                onClick={() => openItem(n)}
                data-testid={`notification-${n.notification_id}`}
                className={`w-full text-left p-3 border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors ${!n.read ? "bg-primary/5" : ""}`}
              >
                <div className="flex gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${n.kind?.startsWith("demo") ? "bg-primary/10 text-primary" : "bg-muted"}`}>
                    <Calendar className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{n.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {!n.read && <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-2" />}
                </div>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
